import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import {
  OrganizationProvider,
  useOrganization,
} from "./context/OrganizationContext";
import { DeveloperProvider, useDeveloper } from "./context/DeveloperContext";
import { DevOverrideProvider } from "./context/DevOverrideContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import About from "./components/about/About";
import Login from "./components/auth/Login";
import AuthLoadingScreen from "./components/auth/AuthLoadingScreen";
import PasswordChange from "./components/auth/PasswordChange";
import ResetPassword from "./components/auth/ResetPassword";
import StudentDashboard from "./components/student/StudentDashboard";
import TeacherDashboard from "./components/teacher/TeacherDashboard";
import AdminPanel from "./components/admin/AdminPanel";
import DeveloperDashboard from "./components/developer/DeveloperDashboard";
import BillingRequired from "./components/billing/BillingRequired";
import BillingWarningBanner from "./components/billing/BillingWarningBanner";
import UpdateAvailableBanner from "./components/common/UpdateAvailableBanner";

// Protected route wrapper that checks billing
export const RequireBilling = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const { billingStatus } = useOrganization();

  if (!billingStatus?.active) {
    // billingStatus (Partial<BillingStatusResponse> & { active }) carries no
    // `message` field, so the legacy `message={billingStatus?.message}` always
    // resolved to undefined and BillingRequired fell back to its own default
    // copy — passing nothing here is behavior-identical.
    return <BillingRequired />;
  }

  return (
    <>
      <BillingWarningBanner />
      {children}
    </>
  );
};

// Developer-only route wrapper
const RequireDeveloper = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactNode => {
  const { userRole } = useAuth();

  if (userRole !== "developer") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Wrapper to provide DevOverrideContext with DeveloperContext values
const DeveloperDevOverrideWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const developerContext = useDeveloper();

  return (
    <DevOverrideProvider orgContextValue={developerContext}>
      {children}
    </DevOverrideProvider>
  );
};

// Wrapper to provide DevOverrideContext with OrganizationContext values
const OrganizationDevOverrideWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const organizationContext = useOrganization();

  return (
    <DevOverrideProvider orgContextValue={organizationContext}>
      {children}
    </DevOverrideProvider>
  );
};

// Developer-only routes that don't need OrganizationProvider
const DeveloperRoutes = () => {
  return (
    <Routes>
      <Route
        path="/developer"
        element={
          <RequireDeveloper>
            <DeveloperDashboard />
          </RequireDeveloper>
        }
      />
      <Route path="*" element={<Navigate to="/developer" replace />} />
    </Routes>
  );
};

// AdminPanel wrapper for regular users
const AdminPanelWithContext = (): React.ReactElement => {
  // OrganizationContext resolves currentOrgId to String(currentUser.school.id);
  // AdminPanel (and every school-scoped API/panel it feeds) works in numeric
  // org ids, so re-narrow the id back to a number here. Number(String(id))
  // recovers the original school.id, keeping behavior identical.
  const { currentOrgId } = useOrganization();
  return (
    <AdminPanel
      organizationContext={{
        currentOrgId: currentOrgId != null ? Number(currentOrgId) : null,
      }}
    />
  );
};

// Logout route — actually sign the user out (clear the session + local auth
// state via AuthContext.logout), then redirect home. Mirrors the
// await-logout pattern used by BillingRequired's Sign Out button. Shows the
// loading screen while the async sign-out is in flight so we never redirect
// before auth state is cleared.
export const LogoutRoute = () => {
  const { logout } = useAuth();
  const [done, setDone] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        await logout();
      } finally {
        setDone(true);
      }
    })();
    // Run once on mount (guarded by the started ref so StrictMode's dev
    // double-invoke does not issue a second sign-out); logout is stable
    // from AuthContext.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!done) {
    return <AuthLoadingScreen />;
  }

  return <Navigate to="/" replace />;
};

// Regular user routes that need OrganizationProvider
const RegularUserRoutes = () => {
  const { currentUser, userRole, loading: authLoading } = useAuth();
  const { loading: orgLoading } = useOrganization();

  // Wait for both auth and organization contexts to be ready
  const loading = authLoading || (currentUser && orgLoading);

  const getDefaultRoute = () => {
    // Route based on role
    if (!currentUser) {
      return "/";
    }

    switch (userRole) {
      case "admin":
        return "/admin";
      case "teacher":
        return "/teacher";
      case "student":
        return "/student";
      default:
        return "/student";
    }
  };

  return (
    <Routes>
      {/* Home route - redirect if not authenticated, redirect if authenticated */}
      <Route
        path="/"
        element={
          !currentUser ? (
            <Navigate to="/login" replace />
          ) : loading || userRole === null ? (
            <AuthLoadingScreen />
          ) : (
            <Navigate to={getDefaultRoute()} replace />
          )
        }
      />

      {/* Login route - always show login page */}
      <Route
        path="/login"
        element={
          !currentUser ? (
            <Login />
          ) : loading || userRole === null ? (
            <AuthLoadingScreen />
          ) : (
            <Navigate to={getDefaultRoute()} replace />
          )
        }
      />

      {/* Reset-password route — reachable without a session (link from a
          password-reset or invite email) */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* About route — public, reachable regardless of auth state */}
      <Route path="/about" element={<About />} />

      {/* Student dashboard */}
      <Route
        path="/student"
        element={
          <ProtectedRoute allowedRoles={["student", "teacher", "admin"]}>
            <RequireBilling>
              <StudentDashboard />
            </RequireBilling>
          </ProtectedRoute>
        }
      />

      {/* Teacher dashboard */}
      <Route
        path="/teacher"
        element={
          <ProtectedRoute allowedRoles={["teacher", "admin"]}>
            <RequireBilling>
              <TeacherDashboard />
            </RequireBilling>
          </ProtectedRoute>
        }
      />

      {/* Admin panel */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <RequireBilling>
              <AdminPanelWithContext />
            </RequireBilling>
          </ProtectedRoute>
        }
      />

      {/* Password change route for students */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute allowedRoles={["student"]}>
            <PasswordChange
              required={false}
              onComplete={() => (window.location.href = "/student")}
            />
          </ProtectedRoute>
        }
      />

      {/* Logout route */}
      <Route path="/logout" element={<LogoutRoute />} />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AppRouter = () => {
  const { userRole, loading: authLoading } = useAuth();

  // Show loading while auth is initializing
  if (authLoading) {
    return <AuthLoadingScreen />;
  }

  return (
    <Router>
      {userRole === "developer" ? (
        <DeveloperProvider>
          <DeveloperDevOverrideWrapper>
            <DeveloperRoutes />
          </DeveloperDevOverrideWrapper>
        </DeveloperProvider>
      ) : (
        <OrganizationProvider>
          <OrganizationDevOverrideWrapper>
            <RegularUserRoutes />
          </OrganizationDevOverrideWrapper>
        </OrganizationProvider>
      )}
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <UpdateAvailableBanner />
        <AppRouter />
      </div>
    </AuthProvider>
  );
}

export default App;
